(() => {
  if (window.__bbFavouritesPageInitialized) return;
  window.__bbFavouritesPageInitialized = true;

  let favouriteEntries = [];
  let favouritesCart = null;

  function favouritesApi() {
    return window.BuildingBasketFavourites || null;
  }

  function readFavourites() {
    return favouritesApi()?.read?.() || [];
  }

  function writeFavourites(items) {
    return favouritesApi()?.write?.(items) || [];
  }

  function heartSvg(filled = false) {
    return favouritesApi()?.heartSvg?.(filled) || '';
  }

  function formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(amount);
    } catch (error) {
      return `€${amount.toFixed(2)}`;
    }
  }

  async function fetchProduct(handle) {
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Product unavailable (${response.status})`);
    return response.json();
  }

  function selectVariant(product, favourite) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    return (
      variants.find((variant) => String(variant.id) === String(favourite.variantId) && variant.available) ||
      variants.find((variant) => variant.available) ||
      variants.find((variant) => String(variant.id) === String(favourite.variantId)) ||
      variants[0] ||
      null
    );
  }

  function imageUrl(product) {
    const source = product.featured_image || product.images?.[0] || '';
    if (!source) return '';
    if (source.startsWith('//')) return `${window.location.protocol}${source}`;
    return source;
  }

  function setPageStatus(message, isError = false) {
    const status = document.querySelector('[data-bb-favourites-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-error', isError);
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to refresh the basket');
    return response.json();
  }

  function findCartItem(cart, variantId) {
    return cart?.items?.find((item) => String(item.variant_id) === String(variantId)) || null;
  }

  function updateCartBubble(cart) {
    const target = document.getElementById('cart-icon-bubble');
    if (!target || !cart) return;

    const count = Math.max(0, Number(cart.item_count) || 0);
    let bubble = target.querySelector('.cart-count-bubble');

    if (count === 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      target.appendChild(bubble);
    }

    let visibleCount = bubble.querySelector('span[aria-hidden="true"]');
    if (!visibleCount) {
      visibleCount = document.createElement('span');
      visibleCount.setAttribute('aria-hidden', 'true');
      bubble.prepend(visibleCount);
    }
    visibleCount.textContent = String(count);

    let accessibleCount = bubble.querySelector('.visually-hidden');
    if (!accessibleCount) {
      accessibleCount = document.createElement('span');
      accessibleCount.className = 'visually-hidden';
      bubble.appendChild(accessibleCount);
    }
    accessibleCount.textContent = count === 1 ? '1 item in basket' : `${count} items in basket`;
  }

  function updateCardCartState(card, cart) {
    const variantId = card.dataset.bbVariantId || '';
    const available = card.dataset.bbAvailable === 'true';
    const item = findCartItem(cart, variantId);
    const quantity = Math.max(0, Number(item?.quantity) || 0);
    const busy = card.dataset.bbBusy === 'true';
    const addButton = card.querySelector('.bb-favourite-card__add');
    const quantityControl = card.querySelector('[data-bb-favourite-quantity]');
    const quantityValue = card.querySelector('[data-bb-favourite-quantity-value]');
    const quantityButtons = Array.from(card.querySelectorAll('[data-bb-favourite-quantity] button'));

    card.dataset.bbCartQuantity = String(quantity);
    card.dataset.bbLineKey = item?.key || '';

    if (addButton) {
      addButton.hidden = available && quantity > 0;
      addButton.disabled = busy || !available;
      addButton.textContent = available ? 'Add to basket' : 'Unavailable';
    }

    if (quantityControl) quantityControl.hidden = !available || quantity === 0;
    if (quantityValue) quantityValue.textContent = String(quantity);
    quantityButtons.forEach((button) => (button.disabled = busy));
  }

  function updateAddAllButton() {
    const button = document.querySelector('[data-bb-add-all-favourites]');
    if (!button) return;

    const savedHandles = new Set(readFavourites().map((item) => item.handle));
    const availableEntries = favouriteEntries.filter(
      (entry) => savedHandles.has(entry.favourite.handle) && entry.product && entry.variant?.available && !entry.error,
    );
    const missingEntries = favouritesCart
      ? availableEntries.filter((entry) => !findCartItem(favouritesCart, entry.variant.id))
      : availableEntries;

    if (!availableEntries.length) {
      button.disabled = true;
      button.textContent = 'No available products';
    } else if (!missingEntries.length) {
      button.disabled = true;
      button.textContent = 'All in basket';
    } else {
      button.disabled = false;
      button.textContent = 'Add all to basket';
    }
  }

  function syncFavouritesCart(cart) {
    favouritesCart = cart;
    updateCartBubble(cart);
    document.querySelectorAll('[data-bb-favourite-card]').forEach((card) => {
      card.dataset.bbBusy = 'false';
      updateCardCartState(card, cart);
    });
    updateAddAllButton();
  }

  async function addVariantsToCart(variantIds) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: variantIds.map((id) => ({ id, quantity: 1 })) }),
    });
    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Unable to add products to the basket');
    }

    const cart = await fetchCart();
    syncFavouritesCart(cart);
    document.dispatchEvent(new CustomEvent('buildingbasket:cart-updated', { detail: { cart } }));
    return cart;
  }

  async function setVariantQuantity(variantId, quantity) {
    const currentCart = await fetchCart();
    const item = findCartItem(currentCart, variantId);

    if (!item && quantity > 0) return addVariantsToCart([variantId]);
    if (!item) {
      syncFavouritesCart(currentCart);
      return currentCart;
    }

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: Math.max(0, quantity) }),
    });
    const cart = await response.json();
    if (!response.ok || cart.status) {
      throw new Error(cart.description || cart.message || 'Unable to update the basket');
    }

    syncFavouritesCart(cart);
    document.dispatchEvent(new CustomEvent('buildingbasket:cart-updated', { detail: { cart } }));
    return cart;
  }

  function createProductCard(entry) {
    const { favourite, product, variant, error } = entry;
    const card = document.createElement('article');
    card.className = 'bb-favourite-card';
    card.dataset.bbFavouriteCard = favourite.handle;

    const productUrl = product?.url || `/products/${encodeURIComponent(favourite.handle)}`;
    const available = Boolean(product && variant?.available && !error);
    const title = product?.title || favourite.handle.replace(/-/g, ' ');
    card.dataset.bbVariantId = variant?.id ? String(variant.id) : '';
    card.dataset.bbAvailable = String(available);
    card.dataset.bbBusy = 'false';
    card.dataset.bbCartQuantity = '0';

    const favouriteButton = document.createElement('button');
    favouriteButton.type = 'button';
    favouriteButton.className = 'bb-favourite-toggle bb-favourite-card__heart is-saved focus-inset';
    favouriteButton.dataset.bbFavouriteCardToggle = '';
    favouriteButton.setAttribute('aria-pressed', 'true');
    favouriteButton.setAttribute('aria-label', `Remove ${title} from favourites`);
    favouriteButton.innerHTML = `
      <span class="bb-favourite-toggle__icon" aria-hidden="true">${heartSvg(true)}</span>
      <span class="visually-hidden">Remove from favourites</span>`;
    favouriteButton.addEventListener('click', () => {
      writeFavourites(readFavourites().filter((item) => item.handle !== favourite.handle));
      favouriteEntries = favouriteEntries.filter((entry) => entry.favourite.handle !== favourite.handle);
      card.remove();
      setPageStatus(`${title} removed from favourites.`);
      updateEmptyState();
      updateAddAllButton();
    });

    const mediaLink = document.createElement('a');
    mediaLink.className = 'bb-favourite-card__media';
    mediaLink.href = productUrl;
    mediaLink.setAttribute('aria-label', title);

    const image = imageUrl(product || {});
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = title;
      img.loading = 'lazy';
      mediaLink.appendChild(img);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'bb-favourite-card__placeholder';
      placeholder.textContent = 'No image';
      mediaLink.appendChild(placeholder);
    }

    const content = document.createElement('div');
    content.className = 'bb-favourite-card__content';

    const heading = document.createElement('h2');
    heading.className = 'bb-favourite-card__title';
    const titleLink = document.createElement('a');
    titleLink.href = productUrl;
    titleLink.textContent = title;
    heading.appendChild(titleLink);
    content.appendChild(heading);

    if (product && variant) {
      const price = document.createElement('p');
      price.className = 'bb-favourite-card__price';
      price.textContent = formatMoney(variant.price ?? product.price);
      content.appendChild(price);
    }

    if (!available) {
      const unavailable = document.createElement('p');
      unavailable.className = 'bb-favourite-card__unavailable';
      unavailable.textContent = error ? 'This product is no longer available.' : 'Currently unavailable';
      content.appendChild(unavailable);
    }

    const actions = document.createElement('div');
    actions.className = 'bb-favourite-card__actions';

    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'button button--primary bb-favourite-card__add';
    addButton.textContent = available ? 'Add to basket' : 'Unavailable';
    addButton.disabled = !available;
    if (available) {
      addButton.addEventListener('click', async () => {
        card.dataset.bbBusy = 'true';
        addButton.disabled = true;
        addButton.textContent = 'Adding…';
        try {
          await addVariantsToCart([variant.id]);
          setPageStatus(`${title} added to your basket.`);
        } catch (addError) {
          card.dataset.bbBusy = 'false';
          updateCardCartState(card, favouritesCart);
          setPageStatus(addError.message, true);
        }
      });
    }

    const quantityControl = document.createElement('div');
    quantityControl.className = 'bb-favourite-quantity';
    quantityControl.dataset.bbFavouriteQuantity = '';
    quantityControl.hidden = true;
    quantityControl.setAttribute('role', 'group');
    quantityControl.setAttribute('aria-label', `Quantity for ${title}`);

    const minusButton = document.createElement('button');
    minusButton.type = 'button';
    minusButton.className = 'bb-favourite-quantity__button';
    minusButton.textContent = '\u2212';
    minusButton.setAttribute('aria-label', `Decrease quantity for ${title}`);

    const quantityValue = document.createElement('span');
    quantityValue.className = 'bb-favourite-quantity__value';
    quantityValue.dataset.bbFavouriteQuantityValue = '';
    quantityValue.textContent = '0';
    quantityValue.setAttribute('aria-live', 'polite');

    const plusButton = document.createElement('button');
    plusButton.type = 'button';
    plusButton.className = 'bb-favourite-quantity__button';
    plusButton.textContent = '+';
    plusButton.setAttribute('aria-label', `Increase quantity for ${title}`);

    async function changeQuantity(delta) {
      if (card.dataset.bbBusy === 'true') return;
      const currentQuantity = Math.max(0, Number(card.dataset.bbCartQuantity) || 0);
      const desiredQuantity = Math.max(0, currentQuantity + delta);
      card.dataset.bbBusy = 'true';
      updateCardCartState(card, favouritesCart);

      try {
        await setVariantQuantity(variant.id, desiredQuantity);
        setPageStatus(
          desiredQuantity === 0
            ? `${title} removed from your basket.`
            : `${title} quantity updated to ${desiredQuantity}.`,
        );
      } catch (quantityError) {
        card.dataset.bbBusy = 'false';
        updateCardCartState(card, favouritesCart);
        setPageStatus(quantityError.message, true);
      }
    }

    minusButton.addEventListener('click', () => changeQuantity(-1));
    plusButton.addEventListener('click', () => changeQuantity(1));
    quantityControl.append(minusButton, quantityValue, plusButton);

    actions.append(addButton, quantityControl);
    content.appendChild(actions);
    card.append(favouriteButton, mediaLink, content);
    return card;
  }

  function updateEmptyState() {
    const page = document.querySelector('[data-bb-favourites-page]');
    if (!page) return;

    const grid = page.querySelector('[data-bb-favourites-grid]');
    const empty = page.querySelector('[data-bb-favourites-empty]');
    const controls = page.querySelector('[data-bb-favourites-controls]');
    const hasCards = Boolean(grid?.querySelector('[data-bb-favourite-card]'));

    grid.hidden = !hasCards;
    empty.hidden = hasCards;
    controls.hidden = !hasCards;
  }

  async function renderFavouritesPage() {
    const page = document.querySelector('[data-bb-favourites-page]');
    if (!page || page.dataset.bbRendered === 'true') return;
    page.dataset.bbRendered = 'true';

    const favourites = readFavourites();
    const grid = page.querySelector('[data-bb-favourites-grid]');
    const loading = page.querySelector('[data-bb-favourites-loading]');
    const addAllButton = page.querySelector('[data-bb-add-all-favourites]');

    if (!favourites.length) {
      loading.hidden = true;
      updateEmptyState();
      return;
    }

    const entries = await Promise.all(
      favourites.map(async (favourite) => {
        try {
          const product = await fetchProduct(favourite.handle);
          return { favourite, product, variant: selectVariant(product, favourite), error: null };
        } catch (error) {
          return { favourite, product: null, variant: null, error };
        }
      }),
    );

    loading.hidden = true;
    entries.forEach((entry) => grid.appendChild(createProductCard(entry)));
    updateEmptyState();

    favouriteEntries = entries;
    try {
      syncFavouritesCart(await fetchCart());
    } catch (cartError) {
      setPageStatus('Your favourites loaded, but the basket could not be refreshed.', true);
      updateAddAllButton();
    }

    addAllButton.addEventListener('click', async () => {
      const savedHandles = new Set(readFavourites().map((item) => item.handle));
      const currentAvailableEntries = favouriteEntries.filter(
        (entry) => savedHandles.has(entry.favourite.handle) && entry.product && entry.variant?.available && !entry.error,
      );
      if (!currentAvailableEntries.length) return;
      addAllButton.disabled = true;
      addAllButton.textContent = 'Adding…';

      try {
        const currentCart = await fetchCart();
        syncFavouritesCart(currentCart);
        const missingEntries = currentAvailableEntries.filter((entry) => !findCartItem(currentCart, entry.variant.id));
        const alreadyInBasket = currentAvailableEntries.length - missingEntries.length;
        if (!missingEntries.length) {
          setPageStatus('All available favourites are already in your basket.');
          return;
        }

        await addVariantsToCart(missingEntries.map((entry) => entry.variant.id));
        const skipped = savedHandles.size - currentAvailableEntries.length;
        const messages = [
          `${missingEntries.length} ${missingEntries.length === 1 ? 'product' : 'products'} added to your basket.`,
        ];
        if (alreadyInBasket) messages.push(`${alreadyInBasket} already in your basket.`);
        if (skipped) messages.push(`${skipped} unavailable ${skipped === 1 ? 'product was' : 'products were'} skipped.`);
        setPageStatus(messages.join(' '));
      } catch (error) {
        updateAddAllButton();
        setPageStatus(error.message, true);
      }
    });
  }

  function initialize() {
    if (!document.querySelector('[data-bb-favourites-page]')) return;
    renderFavouritesPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
